import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import AdminCursor from '../components/AdminCursor';
import { useAuth } from '../AuthContext';

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate('/admin/dashboard', { replace: true });
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password);
      const home = u.role === 'admin' ? '/admin/dashboard' : '/admin/open-call';
      navigate(home, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-root min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <AdminCursor />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-10">
          <p className="text-xs font-mono tracking-[0.3em] text-[#90E0EF] uppercase">The Relative Gallery</p>
          <p className="text-[10px] font-mono text-[#555] tracking-[0.2em] mt-2">CURATOR OS · LOGIN</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-5">
          <div>
            <label className="text-[10px] font-mono text-[#888] tracking-[0.2em] uppercase block mb-2">EMAIL</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              className="w-full bg-transparent border border-[#333] text-white rounded-none font-mono text-sm h-11 px-3 focus:outline-none focus:border-[#90E0EF]"
              placeholder="you@therelativegallery.com"
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono text-[#888] tracking-[0.2em] uppercase block mb-2">PASSWORD</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full bg-transparent border border-[#333] text-white rounded-none font-mono text-sm h-11 px-3 focus:outline-none focus:border-[#90E0EF]"
              placeholder="Enter password"
              data-testid="admin-login-password"
            />
          </div>

          {error && <p className="text-red-400 text-xs font-mono" data-testid="admin-login-error">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full h-11 bg-[#90E0EF] text-black font-mono text-xs uppercase tracking-[0.2em] rounded-none hover:bg-[#a8e8f4] transition-all duration-300 hover:shadow-[0_0_20px_rgba(144,224,239,0.2)] disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ cursor: 'pointer' }}
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? 'Signing in…' : 'Login'}
          </button>
        </form>

        <p className="text-center text-[9px] font-mono text-[#333] mt-8 tracking-wider">
          THE RELATIVE GALLERY · CURATOR OS
        </p>
      </motion.div>
    </div>
  );
}
